import { useCallback, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCommercialModels } from '@/shared/hooks/useCommercialModels';
import { fetchHasAnySavedCredentials } from '@/features/settings/hooks/useOrganizationProviderSettings';
import { useCanManageProviders } from '@/features/auth/providerAccess';
import {
    WIZARD_ASSISTANT_QUERY_PARAM,
    WIZARD_ASSISTANT_QUERY_VALUE,
} from '@/features/assistant/constants';

export const CREATE_TASK_ROUTE = '/create-task';

/**
 * Path into the model-creation wizard. The wizard shows the Spark assistant
 * on every real visit. `assistant: true` still adds `?assistant=1` (the
 * choice screen's "Jump right in" and "Add new task"); the guided demo tour
 * never sets it and never shows the panel.
 */
export const createTaskPath = ({ assistant = false, projectName = null } = {}) => {
    const params = new URLSearchParams();
    if (projectName) params.set('project', projectName);
    if (assistant) params.set(WIZARD_ASSISTANT_QUERY_PARAM, WIZARD_ASSISTANT_QUERY_VALUE);
    const query = params.toString();
    return query ? `${CREATE_TASK_ROUTE}?${query}` : CREATE_TASK_ROUTE;
};

/**
 * Shared entry point into the model-creation wizard (CreateTaskWizardPage).
 *
 * Every "create a model" CTA (Projects header, Datasets / Models empty cards)
 * goes through the same gate: when no model-provider credentials exist, the
 * first-time Model Providers onboarding modal opens first; otherwise the
 * wizard opens directly.
 *
 * Returns `openCreateModel(options)` plus the onboarding-modal state and
 * handlers the caller passes to `ModelProvidersOnboardingModal`. `options`
 * are the `createTaskPath` options; a click event passed by `onClick` is
 * ignored.
 */
export const useCreateModelEntry = () => {
    const navigate = useNavigate();
    const canManageProviders = useCanManageProviders();
    const { availableProviders } = useCommercialModels();
    const hasProviderCredentials = Object.keys(availableProviders || {}).length > 0;
    const [isProvidersOnboardingOpen, setIsProvidersOnboardingOpen] = useState(false);
    // Remembers "Jump right in" across the providers onboarding modal so the
    // continue path lands on the same assistant-enabled wizard.
    const pendingCreateOptionsRef = useRef({});

    const goToCreateTask = useCallback(
        (options = {}) => {
            navigate(createTaskPath(options));
        },
        [navigate]
    );

    const openCreateModel = useCallback(
        (options = {}) => {
            const createOptions = {
                assistant: options?.assistant === true,
                projectName: typeof options?.projectName === 'string' ? options.projectName : null,
            };
            // A non-empty models catalog proves credentials exist, but an empty
            // one does not prove they are missing (fetch may be in flight, have
            // failed, or the catalog may lag behind a freshly saved key). Before
            // gating, confirm against the org settings — the source of truth.
            // SaaS non-gods cannot add credentials (the keys are SpecificAI's),
            // so the onboarding gate is skipped for them altogether.
            if (hasProviderCredentials || !canManageProviders) {
                goToCreateTask(createOptions);
                return;
            }
            fetchHasAnySavedCredentials((hasSavedCredentials) => {
                if (hasSavedCredentials) {
                    goToCreateTask(createOptions);
                } else {
                    pendingCreateOptionsRef.current = createOptions;
                    setIsProvidersOnboardingOpen(true);
                }
            });
        },
        [goToCreateTask, hasProviderCredentials, canManageProviders]
    );

    const closeProvidersOnboardingModal = useCallback(() => {
        pendingCreateOptionsRef.current = {};
        setIsProvidersOnboardingOpen(false);
    }, []);

    const continueFromProvidersOnboarding = useCallback(() => {
        setIsProvidersOnboardingOpen(false);
        const createOptions = pendingCreateOptionsRef.current;
        pendingCreateOptionsRef.current = {};
        goToCreateTask(createOptions);
    }, [goToCreateTask]);

    return {
        openCreateModel,
        isProvidersOnboardingOpen,
        closeProvidersOnboardingModal,
        continueFromProvidersOnboarding,
    };
};
